import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { FormComponent, FormComponentProps } from '../../../components/form';
import { Option } from '../../../components/form/elements/select';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../../components/common/tabs/tab-container';
import FodderNeedPeriodType from '../../../enums/fodder-need-period-type';
import AnimalGroupsList from './animal-groups-list';
import { FormData } from './create';
import Feedingstuff from '../../../interfaces/feedingstuff';

export default function AnimalGroups(
  name: string,
  animalGroupsClassifier: Option[],
  saveFormData: Function,
  periods: string[]
): FormComponent {
  return ({ ...rest }) => (
    <AnimalGroupsComponent
      name={name}
      animalGroupsClassifier={animalGroupsClassifier}
      saveFormData={saveFormData}
      periods={periods}
      {...rest}
    />
  );
}

export function AnimalGroupsComponent({
  name,
  formValues,
  animalGroupsClassifier,
  saveFormData,
  periods,
}: FormComponentProps & {
  name: string;
  animalGroupsClassifier: Option[];
  saveFormData: Function;
  formValues?: FormData;
  periods: string[];
}) {
  const { t } = useTranslation();
  const tp = (key: string) => t('pages.livestock.fodder_need.'.concat(key));
  const { getValues, setValue, trigger } = useFormContext();

  const [selectedTab, setSelectedTab] = useState(
    formValues.usedPeriods.some(
      (period: any) => period === formValues.selectedTab
    )
      ? formValues.selectedTab
      : formValues.usedPeriods.some(
          (period: any) => period === FodderNeedPeriodType.WINTER
        )
      ? FodderNeedPeriodType.WINTER
      : FodderNeedPeriodType.SUMMER
  );

  let animalGroupsState = {} as {
    [key: string]: {
      [key: string]: {
        feedingstuffs: { [key: string]: {} };
        feedingstuffsCount: number;
      };
    };
  };
  periods.forEach((period) => (animalGroupsState[period] = {}));
  const [state, setState] = useState({
    count: 0,
    animalGroups: animalGroupsState,
  });

  const addAnimalGroup = useCallback(() => {
    setState((state) => {
      periods.forEach((period) => {
        state.animalGroups[period][state.count] = {
          feedingstuffs: {},
          feedingstuffsCount: 0,
        };

        const feedingstuffsObj =
          formValues[name][period][state.count]?.feedingstuffs;
        const feedingstuffsCount = feedingstuffsObj
          ? Object.keys(feedingstuffsObj).length
          : 0;
        for (let i2 = 0; i2 < feedingstuffsCount; i2++) {
          state.animalGroups[period][state.count].feedingstuffs[i2] = {};
        }
        state.animalGroups[period][state.count].feedingstuffsCount =
          feedingstuffsCount;
        return true;
      });
      return {
        ...state,
        count: state.count + 1,
        animalGroups: state.animalGroups,
      };
    });
  }, [formValues, name, periods]);

  const animalGroupsCount = Object.keys(formValues[name][selectedTab]).length;
  for (let i = state.count; i < animalGroupsCount; i++) {
    addAnimalGroup();
  }

  const addFeedingstuff = (period: string, animalGroupKey: string) => {
    const count =
      state.animalGroups[period][animalGroupKey]?.feedingstuffsCount || 0;
    state.animalGroups[period][animalGroupKey].feedingstuffsCount = count + 1;
    state.animalGroups[period][animalGroupKey].feedingstuffs[count] = {};

    setState({ ...state, animalGroups: state.animalGroups });
  };

  const removeAnimalGroup = (rowIndex: number) => {
    setState((state) => {
      periods.forEach((period) => {
        let groups = getValues('animalGroups.'.concat(period));
        delete groups[rowIndex];
        setValue('animalGroups.'.concat(period), groups);

        trigger('animalGroups.'.concat(period));

        delete state.animalGroups[period][rowIndex];
        return true;
      });

      return {
        ...state,
      };
    });
  };

  const removeFeedingstuff = (
    period: string,
    animalGroupKey: string,
    feedingstuffKey: string,
    setFeedingstuffExist: Function
  ) => {
    const feedingStuffs =
      'animalGroups.' + period + '.' + animalGroupKey + '.feedingstuffs';

    setValue(
      feedingStuffs,
      getValues(feedingStuffs).filter(
        (_feedingstuff: Feedingstuff, index: number) =>
          index !== +feedingstuffKey
      )
    );

    trigger(feedingStuffs);

    setState((state) => {
      delete state.animalGroups[period][animalGroupKey].feedingstuffs[
        feedingstuffKey
      ];
      setFeedingstuffExist(
        Object.values(state.animalGroups[period][animalGroupKey].feedingstuffs)
          .length > 0
      );
      return {
        ...state,
      };
    });
  };

  const setTabSelectedProvider = (tab: any, values: any = null) => {
    setValue('selectedTab', tab);
    saveFormData(getValues());
    setSelectedTab(tab);
  };

  return (
    <TabContainer
      selectedTabKey={selectedTab}
      setTab={setTabSelectedProvider}
      tabNavigation={
        <TabNavigation theme={'pages'}>
          {formValues.usedPeriods.map((period: string) => {
            return (
              <TabLink
                key={`tab-${period}-nav`}
                tabKey={period}
                theme={'pages'}
              >
                {tp('tab_'.concat(period))}
              </TabLink>
            );
          })}
        </TabNavigation>
      }
      tabContents={
        <TabContents theme={'pages'}>
          {formValues.usedPeriods.map((period: string) => {
            return (
              <Tab key={`tab-${period}`} tabKey={period}>
                <AnimalGroupsList
                  key={`tab-${period}-list`}
                  name={name.concat('.', period)}
                  period={period}
                  animalGroupsClassifier={animalGroupsClassifier}
                  animalGroups={state.animalGroups}
                  addNewGroup={addAnimalGroup}
                  deleteGroup={removeAnimalGroup}
                  addFeedingstuff={addFeedingstuff}
                  deleteFeedingstuff={removeFeedingstuff}
                  periods={periods}
                  formValues={formValues}
                />
              </Tab>
            );
          })}
        </TabContents>
      }
    />
  );
}
